import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NzMessageService } from 'ng-zorro-antd';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { inDemoMode } from '@lib/remote-login';
import { getValueFromNamespace, setValueInNamespace } from '@lib/store2';
import { FLEET_CARD_ADMIN_ROUTES, normaliseMicros } from '@app/admin/fleet-card/shared/utils';
import { extractResponseBody, IUTMParams } from '@lib/utils';

import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';
import { Session } from '@services/session';
import { ServerApi } from '@services/server-api';
import { FleetCardsDataService } from '@services/fleet-cards-data';
import { FeatureUpgradeService } from '@misc/upsell/upgrade.service';
import { PendoAnalytics } from '@services/pendo-analytics';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';
import {
  GROUP_TYPE,
  IEarlyPromo,
  IEstimatedSavings,
  IEstimatedSavingsServerResponse,
  IFleetBundle,
  IFleetBundleServerResponse,
  IGroupData,
  IServerEarlyPromo,
  ISpamControlData,
  IUpsellData,
} from '@app/admin/fleet-card/shared/nudge-upsells/interfaces';
import { IGenericUpsellDetails, UPSELL_CAMPAIGN_NAMES } from '@misc/upsell/upsell-config';

import { FLEET_CARD_APPLICATION_STATUS } from '@fleet-cards/fleet-card-status';
import { FLEET_CARD_DASHBOARD_ROUTE_PATHS } from '@fleet-cards/utils';
import { BENEFITS_PAGE_EVENTS, DEFAULT_SPEND_MULTIPLIER, DEFAULT_PROMO_MULTIPLIER, CFS_10 } from '@fleet-cards/benefits-refactor/constants';

export enum FLEET_CARDS_UPSELL_TYPE {
  MAINTENANCE_UPSELL = 'maintenance_upsell',
  REPORTS_UPSELL = 'reports_upsell',
  BILLINGS_UPSELL = 'billings_upsell',
  COMPANY_PAGE_UPSELL = 'company_page_upsell',
  DRIVER_DETAIL_UPSELL = 'driver_detail_upsell',
  FUEL_OVERVIEW_UPSELL = 'fuel_overview_upsell',
  FUEL_PURCHASE_UPSELL = 'fuel_purchase_upsell',
  BENEFITS_PAGE_UPSELL = 'benefits_page_upsell',
  GLOBAL_BANNER_UPSELL = 'global_banner_upsell',
  SAVINGS_CALCULATOR_UPSELL = 'savings_calculator_upsell',
}

export const FLEET_CARDS_UPSELL_MAP: Record<string, string> = {
  [FLEET_CARDS_UPSELL_TYPE.MAINTENANCE_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Maintenance Upsell',
  [FLEET_CARDS_UPSELL_TYPE.REPORTS_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Reports Upsell',
  [FLEET_CARDS_UPSELL_TYPE.BILLINGS_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Billings Upsell',
  [FLEET_CARDS_UPSELL_TYPE.COMPANY_PAGE_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Company Page Upsell',
  [FLEET_CARDS_UPSELL_TYPE.DRIVER_DETAIL_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Driver Detail Upsell',
  [FLEET_CARDS_UPSELL_TYPE.FUEL_OVERVIEW_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Fuel Overview Upsell',
  [FLEET_CARDS_UPSELL_TYPE.FUEL_PURCHASE_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Fuel Purchase Upsell',
  [FLEET_CARDS_UPSELL_TYPE.BENEFITS_PAGE_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Benefits Page Upsell',
  [FLEET_CARDS_UPSELL_TYPE.GLOBAL_BANNER_UPSELL]: 'Experiment - Nudge Upsells Phase 2 - Global Banner Upsell',
  [FLEET_CARDS_UPSELL_TYPE.SAVINGS_CALCULATOR_UPSELL]: ''
};

export interface ISavingsEstimateRequest {
  company_id?: number;
  annualized_total_spend?: number;
  annualized_total_fuel_spend?: number;
  annualized_productivity_gain_per_vehicle?: number;
  productivity_per_hour_rate_per_vehicle?: number;
  customer_type?: string;
  fleet_operation_type?: string;
  cashback_percentage?: number;
  fraud_savings_percentage?: number;
  fleet_size?: number;
  fuel_percentage?: number;
  productivity_gain?: number;

  promotional_savings: ISavingsCategory;
  fraud_savings: ISavingsCategory;
  fuel_savings: ISavingsCategory;
  productivity_savings: ISavingsCategory;

  total_savings: number;
  disclaimer: string;
  recipient_email_address?: string[];
}

export interface ISavingsCategory {
  amount: string;
  title: string;
  heading: string;
  subheading: string;
}



@Injectable({
  providedIn: 'root',
})
export class FleetCardsUpsellService {
  private readonly MAX_UPSELLS_PER_DAY: number = 2;
  private readonly DISMISSAL_THRESHOLD: number = 3;
  private readonly VISIBILITY_THRESHOLD: number = 3;
  private readonly LOCAL_STORAGE_NAMESPACE: string = `USER_${this.session.getUserId()}.FLEET_CARD_UPSELLS`;
  private readonly SPAM_CONTROL_KEY: string = 'SPAM_CONTROL';
  private readonly CONTACT_SALES_KEY: string = 'CONTACT_SALES_CTA_CLICKED';
  private readonly COMPLETED_UPSELLS: string = 'COMPLETED_UPSELLS';
  private readonly SAVINGS_CALCULATOR_KEY: string = 'SAVINGS_CALCULATOR_CLICKED';

  private readonly BASE_URL: string = 'fleetcard/w1/company';
  
  private isNudgeExperimentFlagEnabled: boolean = false;
  private isStickyPromoBannerEnabled: boolean = false;

  public isSavingsCalulatorPrimaryCTADisabled: boolean = false;

  constructor(
    private featureFlagService: FleetCardsFeatureFlagService,
    private session: Session,
    private fleetCardsDataService: FleetCardsDataService,
    private api: ServerApi,
    private router: Router,
    private featureUpgradeService: FeatureUpgradeService,
    private pendo: PendoAnalytics,
    private message: NzMessageService,
  ) {
    this.isNudgeExperimentFlagEnabled = this.featureFlagService.isFleetCardsNudgeExperimentsPhase2Enabled();
    this.isStickyPromoBannerEnabled = this.featureFlagService.isStickyPromoBannerEnabled();
  }

  public canShowCardUpsellStickyBanner(): Observable<boolean> {
    if (this.session.isTrialCustomer()) return of(false);
    if (!this.isStickyPromoBannerEnabled) return of(false);
    if (inDemoMode()) return of(false);
    if (!this.session.hasFleetCardAdministrativePermissionEnabled()) return of(false);
    if (this.getContactSalesClickStatus()) return of(false);

    return this.fetchFleetCardApplicationStatus().pipe(
      map((company: IFleetCardCompany) => {
        if (company.status !== FLEET_CARD_APPLICATION_STATUS.UPSELL) return false;
        return true;
      }),
    );
  }

  /**
   * Determines if an upsell can be shown based on various conditions.
   * @param {string} upsellId - The ID of the upsell to check.
   * @returns {Observable<boolean>} - Whether the upsell can be shown.
   */
  public canShowUpsell(upsellId: string): Observable<boolean> {
    if (this.session.isTrialCustomer()) return of(false);
    if (!this.isNudgeExperimentFlagEnabled) return of(false);
    if (inDemoMode()) return of(false);
    if (!this.session.hasFleetCardAdministrativePermissionEnabled()) return of(false);

    return this.fetchFleetCardApplicationStatus().pipe(
      map((company: IFleetCardCompany) => {
        if (company.status !== FLEET_CARD_APPLICATION_STATUS.UPSELL) return false;

        const currentDate: string = this.getCurrentDate();
        const spamControlData: ISpamControlData = this.getSpamControlData();
        const completedUpsells: string[] = this.getCompletedUpsells();

        if (spamControlData.date !== currentDate) {
          spamControlData.date = currentDate;
          spamControlData.shownUpsellIds = [];
          this.setSpamControlData(spamControlData);
        }

        if (
          spamControlData.shownUpsellIds.length >= this.MAX_UPSELLS_PER_DAY &&
          !spamControlData.shownUpsellIds.includes(upsellId)
        ) {
          return false;
        }

        if (this.haveThreeDismissalsBeenRecorded()) {
          return false;
        }

        const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellId);
        if (dismissalData.lastDismissedDate === currentDate) {
          return false;
        }

        if (dismissalData.shownCount >= this.VISIBILITY_THRESHOLD && dismissalData.lastShownDate !== currentDate) {
          return false;
        }

        if (completedUpsells.includes(upsellId)) {
          return false;
        }

        return true;
      }),
    );
  }

  /**
   * Fetches the fleet card application status from the backend API.
   * @returns {Observable<FLEET_CARD_APPLICATION_STATUS>} - The status of the fleet card application.
   */
  private fetchFleetCardApplicationStatus(): Observable<IFleetCardCompany> {
    // Replace with the actual API call returning an observable.
    return this.fleetCardsDataService.getCachedCompanyMetadata();
  }

  /**
   * Marks the upsell as shown by updating spam control and upsell-specific data.
   * @param {string} upsellId - The ID of the upsell being shown.
   */
  public markUpsellAsShown(upsellId: string, groupData: IGroupData): void {
    const currentDate: string = this.getCurrentDate();
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellId);

    if (upsellData.lastShownDate === currentDate) {
      return;
    }

    const spamControlData: ISpamControlData = this.getSpamControlData();
    spamControlData.shownUpsellIds.push(upsellId);
    this.setSpamControlData(spamControlData);

    upsellData.shownCount += 1;
    upsellData.lastShownDate = currentDate;
    this.setUpsellDataInNamespace(upsellId, upsellData);

    const additionalAttributes = { group: groupData.group };
    this.pendo.trackEvent(this.generateEventTag(upsellId, 'View'), additionalAttributes);
  }

  /**
   * Determines if Upsell has been dismissed three times
   * @returns {boolean} - Whether three total dismissals have been recorded.
   */
  public hasUpsellBeenDismissedThreeTimes(upsellId: string): boolean {
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellId);
    return dismissalData.dismissedCount >= this.DISMISSAL_THRESHOLD;
  }

  /**
   * Determines if three total dismissals have been recorded across all upsells.
   * @returns {boolean} - Whether three total dismissals have been recorded.
   */
  public haveThreeDismissalsBeenRecorded(): boolean {
    const upsellIds: string[] = Object.values(FLEET_CARDS_UPSELL_TYPE);
    let totalDismissals: number = 0;

    for (const upsellId of upsellIds) {
      const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellId);
      totalDismissals += dismissalData.dismissedCount || 0;

      if (totalDismissals >= this.DISMISSAL_THRESHOLD) {
        return true;
      }
    }

    return false;
  }

  /**
   * Records an upsell dismissal and updates the corresponding data.
   * @param {string} upsellId - The ID of the upsell being dismissed.
   */
  public dismissUpsell(upsellId: string): void {
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellId);

    dismissalData.dismissedCount += 1;
    dismissalData.lastDismissedDate = currentDate;

    this.setUpsellDataInNamespace(upsellId, dismissalData);
  }

  /**
   * Retrieves upsell-specific data from local storage.
   * @param {string} upsellId - The ID of the upsell to retrieve data for.
   * @returns {IUpsellData} - The data associated with the specified upsell.
   */
  public getUpsellDataFromNamespace(upsellId: string): IUpsellData {
    return getValueFromNamespace(upsellId.toUpperCase(), this.LOCAL_STORAGE_NAMESPACE) || {
      dismissedCount: 0,
      lastDismissedDate: '',
      shownCount: 0,
      lastShownDate: '',
    };
  }

  /**
   * Stores upsell-specific data in local storage.
   * @param {string} upsellId - The ID of the upsell to store data for.
   * @param {IUpsellData} upsellData - The data to store.
   */
  public setUpsellDataInNamespace(upsellId: string, upsellData: IUpsellData): void {
    setValueInNamespace(upsellId.toUpperCase(), upsellData, this.LOCAL_STORAGE_NAMESPACE);
  }

  /**
   * Retrieves user-level spam control data from local storage.
   * @returns {ISpamControlData} - The spam control data for the user.
   */
  private getSpamControlData(): ISpamControlData {
    return getValueFromNamespace(this.SPAM_CONTROL_KEY, this.LOCAL_STORAGE_NAMESPACE) || {
      date: '',
      shownUpsellIds: [],
    };
  }

  /**
   * Stores user-level spam control data in local storage.
   * @param {ISpamControlData} spamControlData - The spam control data to store.
   */
  private setSpamControlData(spamControlData: ISpamControlData): void {
    setValueInNamespace(this.SPAM_CONTROL_KEY, spamControlData, this.LOCAL_STORAGE_NAMESPACE);
  }

  /**
   * Retrieves the contact sales click status from local storage.
   * @returns {boolean} - True if the contact sales button has been clicked, otherwise false.
   */
  public getContactSalesClickStatus(): boolean {
    return getValueFromNamespace(this.CONTACT_SALES_KEY, this.LOCAL_STORAGE_NAMESPACE) || false;
  }

  /**
   * Stores the contact sales click status in local storage.
   * @param {boolean} status - The status indicating if the contact sales button has been clicked.
   */
  private setContactSalesClickStatus(status: boolean): void {
    setValueInNamespace(this.CONTACT_SALES_KEY, status, this.LOCAL_STORAGE_NAMESPACE);
  }

  /**
   * Retrieves the list of completed upsells from local storage.
   * @returns {string []} - The IDs of completed upsells.
   */
  private getCompletedUpsells(): string[] {
    return getValueFromNamespace(this.COMPLETED_UPSELLS, this.LOCAL_STORAGE_NAMESPACE) || [];
  }

  /**
   * Stores the completed upsells IDs in local storage.
   * @param {string []} completedUpsells - The IDs of completed upsells.
   */
  private setCompletedUpsells(completedUpsells: string[]): void {
    setValueInNamespace(this.COMPLETED_UPSELLS, completedUpsells, this.LOCAL_STORAGE_NAMESPACE);
  }

  /**
   * Stores the savings calculator click status in local storage.
   * @param {boolean} status - The status indicating if the contact sales button has been clicked.
   */
  public setSavingsCalculatorClickStatus(status: boolean): void {
    setValueInNamespace(this.SAVINGS_CALCULATOR_KEY, status, this.LOCAL_STORAGE_NAMESPACE);
  }

  /**
   * Stores the savings calculator click status in local storage.
   * @param {boolean} status - The status indicating if the contact sales button has been clicked.
   */
  public getSavingsCalculatorClickStatus(): boolean {
    return getValueFromNamespace(this.SAVINGS_CALCULATOR_KEY, this.LOCAL_STORAGE_NAMESPACE) || false;
  }

  /**
   * Returns the current date in 'YYYY-MM-DD' format.
   * @returns {string} - The current date.
   */
  private getCurrentDate(): string {
    const today: Date = new Date();
    return today.toISOString().split('T')[0];
  }

  public determineGroupAndVariant(estimatedSavingsResponse: IEstimatedSavings): IGroupData {
    const { controlGroupMetadata } = estimatedSavingsResponse;

    if (Object.values(GROUP_TYPE).includes(controlGroupMetadata)) {
      return { group: controlGroupMetadata, estimatedSavings: estimatedSavingsResponse };
    }
    return { group: null };
  }

  public getEstimatedSavings(): Observable<IEstimatedSavings> {
    return this.api
      .get<IEstimatedSavingsServerResponse>(`${this.BASE_URL}/estimated_savings`)
      .pipe(
        map(extractResponseBody),
        map(this.transformEstimatedSavingsResponse),
      );
  }

  public getSavingsEstimatesPdf(data: ISavingsEstimateRequest): Observable<Blob> {
    return this.api
      .postBlob(`${this.BASE_URL}/savings_estimates_pdf`, data)
      .pipe(map(extractResponseBody)) as Observable<Blob>;
  }

  public sendSavingsEstimates(data: ISavingsEstimateRequest): Observable<any> {
    return this.api
      .post(`${this.BASE_URL}/savings_estimates_email`, data)
      .pipe(map(extractResponseBody)) as Observable<any>;
  }

  private transformEstimatedSavingsResponse(response: IEstimatedSavingsServerResponse): IEstimatedSavings {
    const calculatedSpendRequired = normaliseMicros(response?.early_spend_promo?.spend_required ?? 0);
    const calculatedPromoValue = normaliseMicros(response?.early_spend_promo?.promo_value ?? 0);
    const calculatedSpendMultiplier = normaliseMicros(response?.early_spend_promo?.spend_multiplier ?
        response?.early_spend_promo?.spend_multiplier : DEFAULT_SPEND_MULTIPLIER);
    const calculatedPromoMultiplier = normaliseMicros(response?.early_spend_promo?.promo_multiplier ? 
      response?.early_spend_promo?.promo_multiplier : DEFAULT_PROMO_MULTIPLIER);

    const apifleetSize = response?.fleet_size ?? 0;
    const calculatedAnnualizedPromotionalSavings = apifleetSize < CFS_10 ?  
      (calculatedPromoValue > 0) ? calculatedPromoValue : (calculatedPromoMultiplier * apifleetSize) : 
      normaliseMicros(response?.annualized_promotional_savings ?? 0)

    return {
      preApproved: response?.pre_approved, // Todo: remove it
      savingsEstimate: response?.savings_estimate,
      iftaEligible: response?.ifta_eligible,
      fleetSize: apifleetSize,
      annualizedFraudSavings: normaliseMicros(response?.annualized_fraud_savings ?? 0),
      annualizedOperationalSavings: normaliseMicros(response?.annualized_operational_savings ?? 0),
      annualizedHiddenFeeSavings: normaliseMicros(response?.annualized_hidden_fee_savings ?? 0),
      annualizedFuelSavings: normaliseMicros(response?.annualized_fuel_savings ?? 0),
      annualizedTotalSavings: normaliseMicros(response?.annualized_total_savings ?? 0),
      annualizedPromotionalSavings: calculatedAnnualizedPromotionalSavings,
      cashbackPercentage: response?.cashback_percentage ?? '',
      boostedRebates: response?.boosted_rebates?.map(rebate => ({ // Todo: remove it
        partner: rebate?.partner,
        rebateValue: Number(rebate?.rebate_value),
        logoLink: rebate?.logo_link,
      })) ?? [],
      savingsPdfLink: response?.savings_pdf_link ?? '',
      fleetBundleSavings: response?.fleet_bundle_savings,
      controlGroupMetadata: response?.control_group_metadata ?? null,
      earlySpendPromo: {
        spendRequired: calculatedSpendRequired,
        promoValue: calculatedPromoValue,
        spendMultiplier: calculatedSpendMultiplier,
        promoMultiplier: calculatedPromoMultiplier,
      },
      fleetOperationType: response?.fleet_operation_type ?? '',
      annualizedTotalSpend: normaliseMicros(response?.annualized_total_spend ?? 0),
      annualizedTotalFuelSpend: normaliseMicros(response?.annualized_total_fuel_spend ?? 0),
      customerType: response?.customer_type ?? '',
    };
  }

  public getFleetBundle(): Observable<IFleetBundle> {
    return this.api
      .get<IFleetBundleServerResponse>(`${this.BASE_URL}/fleet_bundle`)
      .pipe(
        map(extractResponseBody),
        map(this.transformFleetBundleResponse),
      );
  }

  private transformFleetBundleResponse(response: IFleetBundleServerResponse): IFleetBundle {
    return {
      estimatedRewards: normaliseMicros(response.estimated_rewards),
      contractValue: normaliseMicros(response.contract_value),
      contractType: response.contract_type,
      fleetBundlePercentage: response.fleet_bundle_percentage,
    };
  }

  public handleContactSalesClick(upsellId: string, groupData: IGroupData): void {
    if (this.getContactSalesClickStatus()) {
      this.message.success($localize`Thanks for contacting us. We’ll get in touch soon.`);
      return;
    }

    const utmParams: IUTMParams = {
      utm_medium: 'native',
      utm_source: 'dashboard-app',
      utm_campaign: 'WAPP_2024-10_Cards-Pre-Approved-Pricing',
    };

    const upsellParams: IGenericUpsellDetails = {
      campaign_type: '',
      utm_params: utmParams,
      url_of_most_recent_mst_fill: window.location.href,
      offer_type: 'Contact Sales',
      cta_content: 'Contact Sales',
      fe_test_info: '',
      product_interest: '',
      upsell_product_interest: 'Fleet Cards',
    };

    this.featureUpgradeService.pendoUpsell(upsellParams, true).subscribe(() => {
      this.setContactSalesClickStatus(true);
      this.message.success($localize`Thanks for contacting us. We’ll get in touch soon.`);
      this.updateCompletedUpsells(upsellId);
      const additionalAttributes = { group: groupData?.group };
      this.pendo.trackEvent(this.generateEventTag(upsellId, 'Contact Sales'), additionalAttributes);
    });
  }

  public handleSavingsCalculatorClick(
    upsellId: string,
    campaignName: string,
    offerType: string,
    ctaContent: string,
    trackingKey: string,
    isPrimaryAction: boolean = false,
  ): void {
    const isNotSubmitted: boolean = !this.getSavingsCalculatorClickStatus();
    if(isPrimaryAction && isNotSubmitted) {
      this.isSavingsCalulatorPrimaryCTADisabled = true;
    }

    const utmParams: IUTMParams = {
      utm_medium: 'native',
      utm_source: 'dashboard-app',
      utm_campaign: campaignName,
    };

    const upsellParams: IGenericUpsellDetails = {
      campaign_type: '',
      utm_params: utmParams,
      url_of_most_recent_mst_fill: window.location.href,
      offer_type: offerType,
      cta_content: ctaContent,
      fe_test_info: '',
      product_interest: 'Fleet Cards',
      upsell_product_interest: '',
    };
  
    this.featureUpgradeService.pendoUpsell(upsellParams, true).subscribe(() => {
      if (isPrimaryAction && isNotSubmitted) {
        this.setSavingsCalculatorClickStatus(true);
        this.isSavingsCalulatorPrimaryCTADisabled = false;
        this.message.success($localize`Thanks for contacting us. We'll get in touch soon.`);
      }

      if (trackingKey) {
        const eventName = BENEFITS_PAGE_EVENTS.CTA[trackingKey];
        this.pendo.trackEvent(eventName, {});
      }
    }, error => {
      this.isSavingsCalulatorPrimaryCTADisabled = false;
      this.message.error($localize`An error occurred while processing your request. Please try again later.`);
    });
  }

  public cardUpsellPages(currentUrl: string): boolean {
    const cardUrls = [
      '/fleet-cards/upsell',
      '/fleet-cards/upsell/benefits',
      '/admin/fleet-cards/card-application',
      '/admin/fleet-cards/upsell',
    ];

    return cardUrls.some(url => currentUrl.match(url));
  }

  public handleLearnMoreClick(upsellId: string, groupData: IGroupData): void {
    const additionalAttributes = { group: groupData?.group };
    this.pendo.trackEvent(this.generateEventTag(upsellId, 'Learn More'), additionalAttributes);
    this.router.navigate([FLEET_CARD_DASHBOARD_ROUTE_PATHS.UPSELL_BENEFITS]);
  }

  public handleApplyNowClick(upsellId: string, groupData: IGroupData): void {
    const additionalAttributes = { group: groupData?.group };
    this.pendo.trackEvent(this.generateEventTag(upsellId, 'Apply Now'), additionalAttributes);

    if (this.featureFlagService.isFleetCardsLeadGenForSE1to29Enabled() &&
      groupData?.group === GROUP_TYPE.estimatedSavings1to9 ||
      groupData?.group === GROUP_TYPE.averageSavings1to9 ||
      groupData?.group === GROUP_TYPE.estimatedSavingsUM10to29 ||
      groupData?.group === GROUP_TYPE.estimatedSavingsUM30to49
    ) {
      this.featureUpgradeService.genericUpsell(UPSELL_CAMPAIGN_NAMES.fleet_cards_estimated_savings_apply_now).subscribe();
    }

    const applicationSubmitEvent: string = this.generateEventTag(upsellId, 'Card Application Submit');
    this.router.navigate([FLEET_CARD_ADMIN_ROUTES.CARD_APPLICATION],
      {
        state:
        {
          onSubmitEvent: applicationSubmitEvent,
          isUpsellRedirect: true,
          groupData,
        },
      });
    this.updateCompletedUpsells(upsellId);
  }

  private updateCompletedUpsells(upsellId: string): void {
    const completedUpsells: string[] = this.getCompletedUpsells();
    completedUpsells.push(upsellId);
    this.setCompletedUpsells(completedUpsells);
  }

  public generateEventTag(upsellId: string, action: string): string {
    const eventName = FLEET_CARDS_UPSELL_MAP[upsellId] || '';
    return `${eventName} - ${action}`;
  }
}